<template>
  <v-expansion-panel>
    <v-expansion-panel-header id="ownersList" class="pl-3">
      <div>
        <v-icon> mdi-account-group-outline </v-icon>
        <span class="ml-1">Owners</span>
      </div>
    </v-expansion-panel-header>
    <v-expansion-panel-content style="padding-left: 0px">
      <template v-for="(owner, index) in application.owners">
        <v-list-item dense :key="index" :selectable="false">
          <v-list-item-icon style="margin-right: 10px">
            <graph-photo-avatar
              :photo-url="owner && owner.photoUrl ? owner.photoUrl : ''"
              :fallback-text="ownerInitials(owner)"
              :color="ownerAvatarColor(owner)"
              :size="28"
            />
          </v-list-item-icon>
          <v-list-item-content style="text-align: left" class="py-1">
            <div class="owner-row">
              <div class="owner-text">
                <v-list-item-title
                  :id="'ownerId_' + index"
                  class="owner-title"
                >
                  {{ ownerPrimaryText(owner) }}
                </v-list-item-title>
                <v-list-item-subtitle v-if="ownerSecondaryText(owner)" class="owner-subtitle">
                  {{ ownerSecondaryText(owner) }}
                </v-list-item-subtitle>
              </div>
              <div class="owner-chip-group">
                <template v-for="(chip, chipIndex) in ownerChips(owner)">
                  <v-tooltip
                    v-if="chip.kind === 'ambiguous'"
                    :key="'owner-chip-tip-' + index + '-' + chipIndex"
                    bottom
                    max-width="520"
                  >
                    <template v-slot:activator="{ on, attrs }">
                      <v-chip
                        x-small
                        label
                        outlined
                        v-bind="attrs"
                        v-on="on"
                        :color="chip.color"
                        class="ml-2 owner-chip"
                      >
                        <v-icon x-small left>{{ chip.icon }}</v-icon>
                        {{ chip.label }}
                      </v-chip>
                    </template>
                    <div class="owner-ambiguity-tooltip">
                      <div
                        v-for="(line, lineIndex) in ambiguityAlternateLines(owner)"
                        :key="'owner-ambiguity-line-' + index + '-' + lineIndex"
                        class="owner-ambiguity-line"
                      >
                        {{ line }}
                      </div>
                    </div>
                  </v-tooltip>
                  <v-chip
                    v-else
                    :key="'owner-chip-' + index + '-' + chipIndex"
                    x-small
                    label
                    outlined
                    :color="chip.color"
                    class="ml-2 owner-chip"
                  >
                    <v-icon v-if="chip.icon" x-small left>{{ chip.icon }}</v-icon>
                    {{ chip.label }}
                  </v-chip>
                </template>
              </div>
            </div>
          </v-list-item-content>
          <v-list-item-icon>
            <v-icon
              v-if="canManageOwners"
              :id="'deleteOwnerButton_' + index"
              @click="deleteOwner($event, owner)"
              >mdi-trash-can-outline</v-icon
            >
          </v-list-item-icon>
        </v-list-item>
        <v-divider :key="'owner-divider-' + index"></v-divider>
      </template>
      <v-list-item dense class="mt-5">
        <v-list-item-icon style="margin-right: 10px">
          <v-icon>mdi-account-outline</v-icon>
        </v-list-item-icon>
        <v-form
          v-on:submit.prevent="nop"
          style="width: 100%"
        >
          <v-autocomplete
            id="addOwnerInput"
            v-model="selectedOwnerRef"
            :items="ownerSearchItems"
            :loading="ownerSearchLoading"
            :error-messages="ownerSearchError"
            :disabled="!canManageOwners"
            item-text="label"
            item-value="ownerRef"
            :hide-no-data="hideNoDataState"
            :no-data-text="noDataText"
            no-filter
            clearable
            label="Add Owner"
            placeholder="Search owner by name, email, or owner reference"
            @update:search-input="onOwnerSearchInput"
            @change="onOwnerSelectionChanged"
            dense
            color="#006c27"
          >
            <template v-slot:item="{ item, on, attrs }">
              <v-list-item v-bind="attrs" v-on="on" class="owner-option-item" dense>
                <v-list-item-content class="owner-option-content">
                  <div class="d-flex align-center owner-option-left">
                  <graph-photo-avatar
                    :photo-url="item && item.photoUrl ? item.photoUrl : ''"
                    :fallback-text="ownerInitials(item)"
                    :color="ownerAvatarColor(item)"
                    :size="24"
                    class="mr-2"
                  />
                  <div class="owner-option-text">
                    <div class="owner-title owner-option-title">
                      {{ ownerPrimaryText(item) }}
                    </div>
                    <div v-if="ownerSecondaryText(item)" class="owner-subtitle owner-option-subtitle">
                      {{ ownerSecondaryText(item) }}
                    </div>
                  </div>
                </div>
                </v-list-item-content>
                <v-list-item-action class="owner-option-action">
                  <div class="owner-chip-group owner-chip-group--option">
                    <template v-for="(chip, chipIndex) in ownerChips(item)">
                      <v-tooltip
                        v-if="chip.kind === 'ambiguous'"
                        :key="'owner-option-chip-tip-' + chipIndex"
                        bottom
                        max-width="520"
                      >
                        <template v-slot:activator="{ on: chipOn, attrs: chipAttrs }">
                          <v-chip
                            x-small
                            label
                            outlined
                            v-bind="chipAttrs"
                            v-on="chipOn"
                            :color="chip.color"
                            class="owner-chip"
                          >
                            <v-icon x-small left>{{ chip.icon }}</v-icon>
                            {{ chip.label }}
                          </v-chip>
                        </template>
                        <div class="owner-ambiguity-tooltip">
                          <div
                            v-for="(line, lineIndex) in ambiguityAlternateLines(item)"
                            :key="'owner-option-ambiguity-line-' + lineIndex"
                            class="owner-ambiguity-line"
                          >
                            {{ line }}
                          </div>
                        </div>
                      </v-tooltip>
                      <v-chip
                        v-else
                        :key="'owner-option-chip-' + chipIndex"
                        x-small
                        label
                        outlined
                        :color="chip.color"
                        class="owner-chip"
                      >
                        <v-icon v-if="chip.icon" x-small left>{{ chip.icon }}</v-icon>
                        {{ chip.label }}
                      </v-chip>
                    </template>
                  </div>
                </v-list-item-action>
              </v-list-item>
            </template>
            <template v-slot:selection="{ item }">
              <div class="d-flex align-center owner-selection-wrap">
                <graph-photo-avatar
                  :photo-url="item && item.photoUrl ? item.photoUrl : ''"
                  :fallback-text="ownerInitials(item)"
                  :color="ownerAvatarColor(item)"
                  :size="20"
                  class="mr-2"
                />
                <span class="owner-selection-text">{{ ownerPrimaryText(item) }}</span>
              </div>
            </template>
            <template v-slot:append-item>
              <v-list-item v-if="hasMoreOwnerResults" dense>
                <v-btn
                  text
                  small
                  color="#006c27"
                  :loading="ownerSearchLoading"
                  @click.stop="loadMoreOwners"
                >
                  Load more results
                </v-btn>
              </v-list-item>
            </template>
          </v-autocomplete>
        </v-form>
        <v-list-item-icon>
          <v-btn
            icon
            id="addOwnerButton"
            :loading="loading"
            :disabled="!canManageOwners || !selectedOwnerRef || dataLoading || !selectedOwnerIsAddable"
            @click="addOwner"
          >
            <v-icon>mdi-account-plus-outline</v-icon>
            <template v-slot:loader>
              <span class="custom-loader">
                <v-icon light>mdi-cached</v-icon>
              </span>
            </template>
          </v-btn>
        </v-list-item-icon>
      </v-list-item>
      <v-list-item v-if="!canManageOwners" dense>
        <v-list-item-content>
          <v-list-item-subtitle>
            You are not an owner or admin of this application and cannot manage owners.
          </v-list-item-subtitle>
        </v-list-item-content>
      </v-list-item>
    </v-expansion-panel-content>
    <v-snackbar :timeout="3000" v-model="showSnackError" bottom color="#006c27">
      {{ snackErrorMessage }}
    </v-snackbar>
    <confirm-delete-overlay
      v-model="confirmDelete"
      @confirm="onRealDeleteOwner"
    >
    </confirm-delete-overlay>
  </v-expansion-panel>
</template>

<script>
import cass from "../api/cass";
import ConfirmDeleteOverlay from "./ConfirmDeleteOverlay.vue";
import GraphPhotoAvatar from "./GraphPhotoAvatar.vue";
import { isOwnerAmbiguous, formatAmbiguityAlternateLine } from "../utils/ownerProjectionAmbiguity";

export default {
  name: "Owners",
  components: {
    ConfirmDeleteOverlay,
    GraphPhotoAvatar,
  },
  props: {
    application: Object,
    dataLoading: Boolean,
  },
  data() {
    return {
      confirmDelete: false,
      confirmedDeleteId: null,
      loading: false,
      selectedOwnerRef: null,
      ownerSearchItems: [],
      ownerSearchLoading: false,
      ownerSearchError: null,
      ownerSearchText: "",
      ownerSearchDebounceHandle: null,
      ownerSearchRequestId: 0,
      ownerSearchPage: 1,
      ownerSearchPageSize: 20,
      ownerSearchTotal: 0,
      selectedOwnerItem: null,
      showSnackError: false,
      snackErrorMessage: "",
    };
  },
  computed: {
    isUserAdmin() {
      return this.$userData && this.$userData.cass && this.$userData.cass.isUserAdmin === true;
    },
    canManageOwners() {
      if (!this.application) {
        return false;
      }
      return this.isUserAdmin || this.application.isCurrentUserOwner === true;
    },
    hasMoreOwnerResults() {
      return (
        this.ownerSearchItems.length > 0 &&
        this.ownerSearchItems.length < this.ownerSearchTotal
      );
    },
    selectedOwnerIsAddable() {
      if (!this.selectedOwnerRef) {
        return false;
      }
      const selected = this.ownerSearchItems.find((item) => item.ownerRef === this.selectedOwnerRef);
      if (selected) {
        return selected.isAddableOwner !== false;
      }
      if (this.selectedOwnerItem && this.selectedOwnerItem.ownerRef === this.selectedOwnerRef) {
        return this.selectedOwnerItem.isAddableOwner !== false;
      }
      return false;
    },
    hasSearchQuery() {
      return this.ownerSearchText.trim().length >= 3;
    },
    hideNoDataState() {
      if (this.ownerSearchLoading) {
        return true;
      }
      return !this.hasSearchQuery;
    },
    noDataText() {
      if (!this.hasSearchQuery || this.ownerSearchLoading) {
        return "";
      }
      return "No onboarded user found";
    },
  },
  beforeDestroy() {
    if (this.ownerSearchDebounceHandle != null) {
      clearTimeout(this.ownerSearchDebounceHandle);
    }
  },

  methods: {
    onRealDeleteOwner() {
      if (!this.canManageOwners) {
        return;
      }
      console.log(this.confirmedDeleteId);
      cass
        .deleteOwner(this.application.applicationId, this.confirmedDeleteId)
        .then((response) => {
          console.log(response);
          this.$emitter.emit("cert_update");
        })
        .catch((err) => {
          console.log(err);
          console.log(err.response);
          this.snackErrorMessage =
            err?.response?.data?.message ||
            "Could not remove owner. Please retry.";
          this.showSnackError = true;
        });
    },
    retry() {
      this.$emit("retry");
    },
    nop() {},
    onOwnerSearchInput(value) {
      this.ownerSearchText = value || "";
      this.ownerSearchError = null;
      if (this.ownerSearchDebounceHandle != null) {
        clearTimeout(this.ownerSearchDebounceHandle);
      }

      const query = this.ownerSearchText.trim();
      if (query.length < 3) {
        this.ownerSearchRequestId += 1;
        this.ownerSearchItems = [];
        this.ownerSearchPage = 1;
        this.ownerSearchTotal = 0;
        this.ownerSearchLoading = false;
        return;
      }

      this.ownerSearchDebounceHandle = setTimeout(() => {
        this.loadOwnerCandidates(query, { append: false });
      }, 300);
    },
    onOwnerSelectionChanged(value) {
      if (!value) {
        this.selectedOwnerItem = null;
        return;
      }
      const selected = this.ownerSearchItems.find((item) => item.ownerRef === value);
      if (selected) {
        this.selectedOwnerItem = selected;
      }
    },
    loadOwnerCandidates(query, options = {}) {
      if (!this.canManageOwners) {
        this.ownerSearchItems = [];
        return;
      }
      const append = options.append === true;
      const requestQuery = query.trim();
      if (requestQuery.length < 3) {
        return;
      }

      const nextPage = append ? this.ownerSearchPage + 1 : 1;
      const requestId = ++this.ownerSearchRequestId;
      this.ownerSearchLoading = true;
      cass
        .searchOwners(
          this.application.applicationId,
          requestQuery,
          nextPage,
          this.ownerSearchPageSize
        )
        .then((response) => {
          if (requestId !== this.ownerSearchRequestId) {
            return;
          }

          if (requestQuery !== this.ownerSearchText.trim()) {
            return;
          }

          const items = response.data.items || [];
          const mappedItems = items
            .filter((item) => item?.isAddableOwner !== false)
            .map((item) => ({
              ...item,
              label: this.formatOwnerLabel(item),
            }));
          if (append) {
            const merged = [...this.ownerSearchItems, ...mappedItems];
            const seen = new Set();
            this.ownerSearchItems = merged.filter((item) => {
              if (!item || !item.ownerRef || seen.has(item.ownerRef)) {
                return false;
              }
              seen.add(item.ownerRef);
              return true;
            });
          } else {
            this.ownerSearchItems = mappedItems;
          }
          this.ownerSearchPage = response.data.page || nextPage;
          this.ownerSearchTotal = response.data.total || this.ownerSearchItems.length;

          if (this.selectedOwnerRef) {
            const selected = this.ownerSearchItems.find(
              (item) => item.ownerRef === this.selectedOwnerRef
            );
            if (selected) {
              this.selectedOwnerItem = selected;
            }
          }
        })
        .catch((err) => {
          if (requestId !== this.ownerSearchRequestId) {
            return;
          }
          const responseMessage = err?.response?.data?.message;
          this.ownerSearchError =
            responseMessage || "Could not search owners. Please retry.";
        })
        .finally(() => {
          if (requestId === this.ownerSearchRequestId) {
            this.ownerSearchLoading = false;
          }
        });
    },
    loadMoreOwners() {
      const query = this.ownerSearchText.trim();
      if (query.length < 3 || !this.hasMoreOwnerResults || this.ownerSearchLoading) {
        return;
      }
      this.loadOwnerCandidates(query, { append: true });
    },
    addOwner() {
      if (!this.canManageOwners) {
        return;
      }
      this.loading = true;
      console.log(this.selectedOwnerRef);
      cass
        .addOwner(this.application.applicationId, this.selectedOwnerRef)
        .then((response) => {
          this.selectedOwnerRef = null;
          this.selectedOwnerItem = null;
          this.ownerSearchItems = [];
          this.ownerSearchText = "";
          this.ownerSearchPage = 1;
          this.ownerSearchTotal = 0;
          console.log(response);
          this.$emitter.emit("cert_update");
        })
        .catch((err) => {
          console.log(err);
          console.log(err.response);
          this.snackErrorMessage =
            err?.response?.data?.message || "Could not add owner. Please retry.";
          this.showSnackError = true;
        })
        .finally(() => (this.loading = false));
    },
    deleteOwner(evt, owner) {
      if (!this.canManageOwners) {
        return;
      }
      evt.stopPropagation();
      this.confirmedDeleteId = owner.ownerRef;
      this.confirmDelete = true;
    },
    formatOwnerLabel(owner) {
      if (!owner) {
        return "";
      }
      const category = this.ownerCategory(owner);
      if (category === "unresolved") {
        return "Unresolved: " + (owner.ownerRef || "unknown") + " - " + this.unresolvedReasonText(owner.unresolvedReasonCode);
      }

      const primary = this.ownerPrimaryText(owner);
      const secondary = this.ownerSecondaryText(owner);
      if (secondary) {
        return primary + " - " + secondary;
      }
      return primary;
    },
    ownerCategory(owner) {
      if (!owner) {
        return "unresolved";
      }
      if (owner.isUnresolved === true || !owner.ownerType) {
        return "unresolved";
      }
      if (owner.ownerType === "internal") {
        return "internal";
      }
      if (owner.ownerType === "external") {
        return "external";
      }
      return "unresolved";
    },
    ownerPrimaryText(owner) {
      const category = this.ownerCategory(owner);
      const displayName = owner?.displayName ? owner.displayName.trim() : "";
      const email = owner?.email ? owner.email.trim() : "";
      const ext14 = owner?.ext14 ? owner.ext14.trim() : "";
      const ownerRef = owner?.ownerRef ? owner.ownerRef.trim() : "";

      if (category === "unresolved") {
        return "Unresolved owner: " + (ownerRef || "unknown");
      }
      if (category === "internal") {
        if (displayName) {
          return displayName;
        }
        if (ext14) {
          return ext14;
        }
        if (email) {
          return email;
        }
        return ownerRef || "Unknown corporate owner";
      }

      if (email) {
        return email;
      }
      if (ownerRef) {
        return ownerRef;
      }
      if (displayName) {
        return displayName;
      }
      return "Unknown external owner";
    },
    ownerSecondaryText(owner) {
      const category = this.ownerCategory(owner);
      const displayName = owner?.displayName ? owner.displayName.trim() : "";
      const email = owner?.email ? owner.email.trim() : "";
      const ext14 = owner?.ext14 ? owner.ext14.trim() : "";
      const ownerRef = owner?.ownerRef ? owner.ownerRef.trim() : "";

      if (category === "unresolved") {
        const reason = this.unresolvedReasonText(owner?.unresolvedReasonCode);
        return "Owner could not be verified (" + reason + ")";
      }
      if (category === "internal") {
        const details = [];
        if (ext14) {
          details.push("ext14: " + ext14);
        }
        if (email) {
          details.push(email);
        }
        return details.join(" | ");
      }

      if (email && ownerRef && email.toLowerCase() !== ownerRef.toLowerCase()) {
        return ownerRef;
      }
      if (!email && ownerRef && displayName && ownerRef.toLowerCase() !== displayName.toLowerCase()) {
        return displayName;
      }
      return "";
    },
    ownerChipLabel(owner) {
      const category = this.ownerCategory(owner);
      if (category === "internal") {
        return "Corporate";
      }
      if (category === "external") {
        return "External";
      }
      return "Unresolved";
    },
    isAmbiguousOwner(owner) {
      return isOwnerAmbiguous(owner);
    },
    ambiguityChipLabel(owner) {
      const count = Number.isInteger(owner && owner.candidateCount) ? owner.candidateCount : 2;
      return "Ambiguous (" + count + ")";
    },
    ambiguityAlternateLines(owner) {
      if (!this.isAmbiguousOwner(owner)) {
        return [];
      }
      const alternates = Array.isArray(owner && owner.candidates) ? owner.candidates : [];
      return alternates.map((alternate) => formatAmbiguityAlternateLine(alternate));
    },
    ownerChips(owner) {
      const category = this.ownerCategory(owner);
      const chips = [];
      if (category === "unresolved") {
        chips.push({
          kind: "unresolved",
          label: "Unresolved",
          color: "#ef6c00",
          icon: null,
        });
      }
      if (this.isAmbiguousOwner(owner)) {
        chips.push({
          kind: "ambiguous",
          label: this.ambiguityChipLabel(owner),
          color: "#c62828",
          icon: "mdi-alert-circle-outline",
        });
      }
      if (category === "internal") {
        chips.push({
          kind: "type",
          label: "Corporate",
          color: "#006c27",
          icon: null,
        });
      }
      if (category === "external") {
        chips.push({
          kind: "type",
          label: "External",
          color: "#1d9bb2",
          icon: null,
        });
      }
      return chips;
    },
    ownerChipColor(owner) {
      const category = this.ownerCategory(owner);
      if (category === "internal") {
        return "#006c27";
      }
      if (category === "external") {
        return "#1d9bb2";
      }
      return "#ef6c00";
    },
    ownerAvatarColor(owner) {
      const category = this.ownerCategory(owner);
      if (category === "internal") {
        return "#006c27";
      }
      if (category === "external") {
        return "#1d9bb2";
      }
      return "#ef6c00";
    },
    ownerInitials(owner) {
      if (!owner) {
        return "?";
      }
      const displayName = owner.displayName ? owner.displayName.trim() : "";
      if (displayName) {
        const parts = displayName.split(/\s+/).filter((part) => part.length > 0);
        if (parts.length >= 2) {
          return (parts[0].charAt(0) + parts[1].charAt(0)).toUpperCase();
        }
        return parts[0].charAt(0).toUpperCase();
      }
      const fallback = owner.ext14 || owner.email || owner.ownerRef || "?";
      const normalized = String(fallback).replace(/[^a-zA-Z0-9]/g, "").toUpperCase();
      if (normalized.length >= 2) {
        return normalized.slice(0, 2);
      }
      if (normalized.length === 1) {
        return normalized;
      }
      return "?";
    },
    unresolvedReasonText(reasonCode) {
      const reasonMap = {
        NO_RUNTIME_PROJECTION: "Owner mapping is missing",
        NO_ENTRA_MATCH: "No matching user account found",
        MISSING_GRAPH_MAIL: "Corporate email is missing",
        MISSING_SAP_ACCOUNT: "No SAP account linked",
      };
      if (!reasonCode) {
        return "Unknown reason";
      }
      return reasonMap[reasonCode] || String(reasonCode).replaceAll("_", " ").toLowerCase();
    },
  },
};
</script>

<style scoped>
.owner-title {
  font-size: 0.92rem;
  line-height: 1.2rem;
  font-weight: 500;
}

.owner-row {
  display: flex;
  align-items: center;
  justify-content: space-between;
  width: 100%;
}

.owner-text {
  min-width: 0;
  flex: 1 1 auto;
}

.owner-subtitle {
  font-size: 0.78rem;
  line-height: 1.1rem;
  color: rgba(0, 0, 0, 0.65);
}

.owner-chip {
  font-size: 0.66rem;
  font-weight: 600;
  align-self: center;
}

.owner-chip-group {
  display: inline-flex;
  align-items: center;
  flex-wrap: wrap;
  justify-content: flex-end;
  gap: 4px;
}

.owner-ambiguity-tooltip {
  display: flex;
  flex-direction: column;
  gap: 4px;
}

.owner-ambiguity-line {
  font-size: 0.78rem;
  line-height: 1.1rem;
}

.owner-option-item {
  min-height: 44px;
}

.owner-option-content {
  padding-top: 4px;
  padding-bottom: 4px;
}

.owner-option-left {
  min-width: 0;
}

.owner-option-text {
  min-width: 0;
  flex: 1 1 auto;
}

.owner-option-action {
  margin: 0 0 0 8px;
  align-self: center;
}

.owner-option-title {
  font-size: 0.86rem;
  line-height: 1.05rem;
}

.owner-option-subtitle {
  font-size: 0.74rem;
  line-height: 1rem;
}

.owner-selection-wrap {
  max-width: 100%;
}

.owner-selection-text {
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  display: inline-block;
  max-width: 100%;
}

.custom-loader {
  animation: loader 1s infinite;
  display: flex;
}
@-moz-keyframes loader {
  from {
    transform: rotate(0);
  }
  to {
    transform: rotate(360deg);
  }
}
@-webkit-keyframes loader {
  from {
    transform: rotate(0);
  }
  to {
    transform: rotate(360deg);
  }
}
@-o-keyframes loader {
  from {
    transform: rotate(0);
  }
  to {
    transform: rotate(360deg);
  }
}
@keyframes loader {
  from {
    transform: rotate(0);
  }
  to {
    transform: rotate(360deg);
  }
}
</style>
